/**
 * useAppStore — Backward-compatible wrapper around Zustand global store.
 *
 * This hook provides the EXACT same public API as the original useState-based
 * implementation, but delegates all state management to the Zustand singleton
 * in app/store.ts. This means:
 * - All consumers share the SAME state (no more split-state bugs)
 * - No consumer code changes needed
 * - Side effects (grade sync, heartbeat, midnight reset) remain as useEffects
 *
 * Refactored as part of Account System Refactor — Fase 1 (Zustand Global Store)
 */

import { useEffect, useMemo, useCallback } from 'react'
import { AppState } from '../types'
import { ModuleId } from '../../content'
import { GroupId, GROUPS } from '../../config/systemConfig'
import { resolvePhaseByDate, getModuleMasteryFromFamilies } from '../../mastery'
import { INITIAL_STATE } from '../../config/initialState'
import {
  getActiveAccount,
  isAllDemoAccounts,
  createFreshState,
  createMasteryQueries,
} from '../../accounts'
import type { AccountsState } from '../../accounts'
import type { LearnerAccount } from '../../accounts/types'
import { debug, warn } from '../../utils/logger'
import { useStore } from '../store'

export function useAppStore() {
  const store = useStore()

  // ── Initialize on first render ─────────────────────────────────────
  useEffect(() => {
    store.initialize()
  }, [])

  // ── Derived State ──────────────────────────────────────────────────
  const multiAccountState = store.multiAccountState
  const isInitialized = store.isInitialized
  const viewingLearnerId = store.viewingLearnerId
  const selectedGroup = store.selectedGroup
  const manualPeriod = store.manualPeriod
  const groupVersion = store.groupVersion

  const activeAccount = multiAccountState
    ? getActiveAccount(multiAccountState.accounts)
    : null

  const state: AppState = useMemo(() => {
    if (!multiAccountState) return INITIAL_STATE
    if (!activeAccount) return INITIAL_STATE
    if (activeAccount.role === 'learner') {
      return multiAccountState.learnerData[activeAccount.id] || INITIAL_STATE
    }
    if (
      (activeAccount.role === 'parent' ||
        activeAccount.role === 'teacher' ||
        activeAccount.role === 'admin') &&
      viewingLearnerId
    ) {
      return multiAccountState.learnerData[viewingLearnerId] || INITIAL_STATE
    }
    return INITIAL_STATE
  }, [multiAccountState, activeAccount, viewingLearnerId])

  const accounts: AccountsState = multiAccountState
    ? multiAccountState.accounts
    : createFreshState().accounts

  const startingPeriod = manualPeriod || resolvePhaseByDate()

  const currentLearnerDisplayName = useMemo(() => {
    if (!activeAccount || !multiAccountState) return INITIAL_STATE.stats.name
    if (activeAccount.role === 'learner') return activeAccount.displayName
    if (viewingLearnerId) {
      const viewed = multiAccountState.accounts.accountsById[viewingLearnerId]
      if (viewed?.role === 'learner') return viewed.displayName
    }
    return activeAccount.displayName
  }, [activeAccount, multiAccountState, viewingLearnerId])

  const requiresViewingLearnerSelection = Boolean(
    activeAccount &&
      activeAccount.role !== 'learner' &&
      activeAccount.role !== 'admin' &&
      !viewingLearnerId,
  )

  const isDemoMode = useMemo(() => {
    if (!multiAccountState) return false
    return isAllDemoAccounts(multiAccountState.accounts)
  }, [multiAccountState])

  // ── Grade → selectedGroup sync ─────────────────────────────────────
  useEffect(() => {
    if (!isInitialized || !activeAccount) return
    if (activeAccount.role === 'learner' && 'grade' in activeAccount) {
      const learnerGrade = (activeAccount as LearnerAccount).grade
      if (
        learnerGrade &&
        GROUPS.includes(learnerGrade as GroupId) &&
        learnerGrade !== selectedGroup
      ) {
        store.setSelectedGroup(learnerGrade as GroupId)
        debug(`📚 Auto-synced selectedGroup to learner grade: ${learnerGrade}`)
      }
    }
  }, [isInitialized, activeAccount?.id, activeAccount?.role])

  // ── Keep lastActive fresh ──────────────────────────────────────────
  useEffect(() => {
    if (!isInitialized || !activeAccount) return
    const touch = () => store.touchLastActive()
    const interval = setInterval(touch, 5 * 60 * 1000)
    const events = ['click', 'keydown', 'scroll', 'touchstart'] as const
    events.forEach((evt) =>
      window.addEventListener(evt, touch, { passive: true }),
    )
    return () => {
      clearInterval(interval)
      events.forEach((evt) => window.removeEventListener(evt, touch))
    }
  }, [isInitialized, activeAccount?.id])

  // ── Daily Goal Midnight Reset ──────────────────────────────────────
  useEffect(() => {
    if (!isInitialized || !activeAccount || activeAccount.role !== 'learner')
      return
    const checkMidnightReset = () => {
      const currentState = useStore.getState()
      const mas = currentState.multiAccountState
      if (!mas) return
      const learnerState = mas.learnerData[activeAccount.id]
      if (!learnerState || !learnerState.dailyStreak) return
      const today = new Date().toISOString().split('T')[0]
      const lastActive = learnerState.dailyStreak.lastActiveDate
      if (today !== lastActive) {
        const newDailyStreak = {
          ...learnerState.dailyStreak,
          todayQuestionsAnswered: 0,
          todayGoalReached: false,
          lastActiveDate: today,
        }
        const todayDate = new Date()
        const lastActiveDateObj = new Date(lastActive)
        const isNewWeek =
          todayDate.getDay() === 1 &&
          (todayDate.getTime() - lastActiveDateObj.getTime() > 86400000 ||
            lastActiveDateObj.getDay() !== 1)
        if (isNewWeek) {
          newDailyStreak.currentWeekDays = 0
        }
        store.updateLearnerState((prev) => ({
          ...prev,
          dailyStreak: newDailyStreak,
        }))
      }
    }
    checkMidnightReset()
    const interval = setInterval(checkMidnightReset, 60 * 1000)
    return () => clearInterval(interval)
  }, [isInitialized, activeAccount?.id, activeAccount?.role])

  // ── Mastery Queries (pure computations from current state) ─────────
  const masteryQueries = createMasteryQueries(
    state,
    selectedGroup,
    manualPeriod,
  )

  // ── Cross-Portal Queries ───────────────────────────────────────────
  const getLearnerData = useCallback(
    (learnerId: string): AppState | null => {
      if (!multiAccountState) return null
      return multiAccountState.learnerData[learnerId] || null
    },
    [multiAccountState],
  )

  const getLearnerModuleMastery = useCallback(
    (learnerId: string, moduleId: ModuleId): number => {
      const data = getLearnerData(learnerId)
      if (!data) return 0
      return getModuleMasteryFromFamilies(
        data.familyMastery || {},
        moduleId,
        selectedGroup,
      )
    },
    [getLearnerData, selectedGroup],
  )

  // ── Public API (IDENTICAL to original) ─────────────────────────────
  return {
    // Derived state
    state,
    accounts,
    activeAccount,
    currentLearnerDisplayName,
    requiresViewingLearnerSelection,
    isInitialized,
    selectedGroup,
    startingPeriod,
    manualPeriod,
    groupVersion,
    viewingLearnerId,
    isDemoMode,
    // Setters
    setSelectedGroup: store.setSelectedGroup,
    setStartingPeriod: store.setStartingPeriod,
    setViewingLearner: store.setViewingLearner,
    // Cross-portal queries
    getLearnerData,
    getLearnerModuleMastery,
    // Dev/demo
    devResetProgress: store.devResetProgress,
    loadDemoAccounts: store.loadDemoAccounts,
    // Account actions (from Zustand store — shared singleton)
    updateProfileDisplayName: store.updateProfileDisplayName,
    updateAccountEmail: store.updateAccountEmail,
    updateAccountNameById: store.updateAccountNameById,
    updateTeacherDetails: store.updateTeacherDetails,
    updateLearnerDetails: store.updateLearnerDetails,
    createAccount: store.createAccount,
    deleteAccount: store.deleteAccount,
    linkParentToLearnerById: store.linkParentToLearnerById,
    linkTeacherToLearnerById: store.linkTeacherToLearnerById,
    unlinkParentFromLearner: store.unlinkParentFromLearner,
    unlinkTeacherFromLearner: store.unlinkTeacherFromLearner,
    setLearnerLinkCode: store.setLearnerLinkCode,
    switchAccount: store.switchAccount,
    clearActiveAccount: store.clearActiveAccount,
    touchLastActive: store.touchLastActive,
    resetLearnerProgress: store.resetLearnerProgress,
    completeOnboarding: store.completeOnboarding,
    createAndLinkStudent: store.createAndLinkStudent,
    addClassroom: store.addClassroom,
    updateClassroom: store.updateClassroom,
    removeClassroom: store.removeClassroom,
    addLearnerToClassroom: store.addLearnerToClassroom,
    removeLearnerFromClassroom: store.removeLearnerFromClassroom,
    joinClassroomByCode: store.joinClassroomByCode,
    regenerateJoinCode: store.regenerateJoinCode,
    transferClassroomOwnership: store.transferClassroomOwnership,
    joinClassroomAsCoTeacher: store.joinClassroomAsCoTeacher,
    // Learner actions (from Zustand store — shared singleton)
    markCheatSheetViewed: store.markCheatSheetViewed,
    recordAnswer: store.recordAnswer,
    clearPendingLevelUp: store.clearPendingLevelUp,
    devJumpToLevel: store.devJumpToLevel,
    savePlacementResult: store.savePlacementResult,
    // Mastery queries (computed from current state)
    ...masteryQueries,
  }
}
